/* eslint-disable operator-linebreak */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import TooltipCheckoutSidebar from '../Tooltips/TooltipCheckoutSidebar';
import TooltipCheckoutSidebarRevised from '../Tooltips/TooltipCheckoutSidebarRevised';
import TooltipCheckoutLCR from '../Tooltips/TooltipCheckoutLCR';

export default function SidebarCheckout({ data, checkout, firstname }) {
  const location = useLocation();
  const [isFirstTriangleOpened, setIsFirstTriangleOpened] = useState(false);
  const [isSecondTriangleOpened, setIsSecondTriangleOpened] = useState(false);
  const [isThirdTriangleOpened, setIsThirdTriangleOpened] = useState(false);
  const [isViewMoreDetailsOpened, setViewMoreDetailsOpened] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  const firstName = checkout?.checkout_object?.customer?.billing?.first_name;

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 851);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const firstTriangle = () => setIsFirstTriangleOpened(!isFirstTriangleOpened);
  const secondTriangle = () => setIsSecondTriangleOpened(!isSecondTriangleOpened);
  const thirdTriangle = () => setIsThirdTriangleOpened(!isThirdTriangleOpened);
  const viewMoreDetails = () => setViewMoreDetailsOpened(!isViewMoreDetailsOpened);

  if (location?.pathname !== '/checkout') {
    return null;
  }

  return (
    <div className="k-checkout-sidebar">
      {!isViewMoreDetailsOpened && (
        <div className={isMobileView ? 'k-checkout-sidebar-content-mobile' : 'k-checkout-sidebar-content'}>
          <div className="k-checkout-sidebar-align">
            <div className="k-checkout-sidebar-block">
              <div className="k-checkout-sidebar-title">Your cart</div>
              <div className="k-checkout-sidebar-value">
                <div className="k-checkout-sidebar-price">${data?.cart_total}</div>
                <button
                  type="button"
                  aria-label="first triangle"
                  className={`k-checkout-sidebar-triangle ${isFirstTriangleOpened ? 'k-triangle-active' : ''}`}
                  onClick={firstTriangle}
                />
              </div>
            </div>
            {isFirstTriangleOpened && (
              <div className="k-checkout-sidebar-block">
                <div className="k-checkout-sidebar-details">
                  <div className="k-checkout-sidebar-left">Initial cart</div>
                  <div className="k-checkout-sidebar-right">${data?.cart_total}</div>
                </div>
                {data?.downpayment && data?.downpayment_amount && (
                  <div className="k-checkout-sidebar-details">
                    <div className="k-checkout-sidebar-left">
                      Lease Cost Reduction
                      <TooltipCheckoutLCR
                        place="bottom"
                        classAdded="k-info-vwo-tooltip"
                        data={{ retailer: checkout?.retailer_name }}
                      />
                    </div>
                    <div className="k-checkout-sidebar-right">-${data?.downpayment_amount}</div>
                  </div>
                )}
                <div className="k-checkout-sidebar-details">
                  <div className="k-checkout-sidebar-left">
                    Revised cart
                    <TooltipCheckoutSidebarRevised place="bottom" classAdded="k-info-vwo-tooltip" />
                  </div>
                  <div className="k-checkout-sidebar-right">${data?.net_cash_price}</div>
                </div>
              </div>
            )}
            <div className="k-checkout-divider" />
            <div className="k-checkout-sidebar-block">
              <div className="k-checkout-sidebar-title">Total due today</div>
              <div className="k-checkout-sidebar-value">
                <div className="k-checkout-sidebar-price">${parseFloat(data?.due_today, 10).toFixed(2)}</div>
                <button
                  type="button"
                  aria-label="second triangle"
                  className={`k-checkout-sidebar-triangle ${isSecondTriangleOpened ? 'k-triangle-active' : ''}`}
                  onClick={secondTriangle}
                />
              </div>
            </div>
            {isSecondTriangleOpened && (
              <div className="k-checkout-sidebar-block">
                <div className="k-checkout-sidebar-details">
                  <div className="k-checkout-sidebar-left">Initial payment</div>
                  <div className="k-checkout-sidebar-right">${parseFloat(data?.due_today, 10).toFixed(2)}</div>
                </div>
                {data.downpayment && data?.downpayment_amount && (
                  <div className="k-checkout-sidebar-details">
                    <div className="k-checkout-sidebar-left">Lease Cost Reduction</div>
                    <div className="k-checkout-sidebar-right">${data?.downpayment_amount}</div>
                  </div>
                )}
              </div>
            )}
            <div className="k-checkout-divider" />
            <div className="k-checkout-sidebar-block">
              <div className="k-checkout-sidebar-with-tooltip">
                <div className="k-checkout-sidebar-title">Recurring amount</div>
                <TooltipCheckoutSidebar place="bottom" classAdded="k-info-vwo-tooltip" />
              </div>
              <div className="k-checkout-sidebar-value">
                <div className="k-checkout-sidebar-price">${data?.monthly_total}</div>
                <button
                  type="button"
                  aria-label="third triangle"
                  className={`k-checkout-sidebar-triangle ${isThirdTriangleOpened && 'k-triangle-active'}`}
                  onClick={thirdTriangle}
                />
              </div>
            </div>
            {isThirdTriangleOpened && (
              <div className="k-checkout-sidebar-block">
                <div className="k-checkout-sidebar-details">
                  <div className="k-checkout-sidebar-left">Next Due Date</div>
                  <div className="k-checkout-sidebar-right">{moment(data?.next_payment_date).format('MM/DD/YYYY')}</div>
                </div>
                <div className="k-checkout-sidebar-details">
                  <div className="k-checkout-sidebar-left">Pay Frequency</div>
                  <div className="k-checkout-sidebar-right k-sidebar-cap">{data?.pay_frequency}</div>
                </div>
                <div className="k-checkout-sidebar-details">
                  <div className="k-checkout-sidebar-left">Lease Duration</div>
                  <div className="k-checkout-sidebar-right">{data?.term} months</div>
                </div>
                <div className="k-checkout-sidebar-details">
                  <div className="k-checkout-sidebar-left">Number of payments</div>
                  <div className="k-checkout-sidebar-right">{data?.num_cycles}</div>
                </div>
              </div>
            )}
            <div className="k-checkout-divider" />
          </div>
          {!isMobileView && (
            <div className="k-checkout-details-block">
              <button
                type="button"
                aria-label="view more details"
                className="k-checkout-view-more-details"
                onClick={viewMoreDetails}
              >
                View More Details
              </button>
            </div>
          )}
        </div>
      )}
      {(isViewMoreDetailsOpened || isMobileView) && (
        <>
          <div
            className={
              isMobileView
                ? 'k-checkout-sidebar-content-mob-top'
                : 'k-checkout-sidebar-content k-checkout-sidebar-view-more'
            }
          >
            <div className="k-checkout-sidebar-align">
              <div className="k-checkout-sidebar-details-title">{firstName || firstname}&apos;s lease details</div>
              <div className="k-checkout-sidebar-scrollable">
                <div className="k-checkout-dark-blue-divider">
                  <div>Due today</div>
                  <div>${parseFloat(data?.due_today, 10).toFixed(2)}</div>
                </div>
                <div className="k-checkout-sidebar-subtitle">Other</div>
                <div className="k-checkout-with-side-margin">
                  <div className="k-checkout-divider" />
                  <div className="k-checkout-sidebar-details">
                    <div className="k-checkout-sidebar-left">Initial payment</div>
                    <div className="k-checkout-sidebar-right">${parseFloat(data?.due_today, 10).toFixed(2)}</div>
                  </div>
                  {data?.downpayment && data?.downpayment_amount && (
                    <div className="k-checkout-sidebar-details">
                      <div className="k-checkout-sidebar-left">Lease cost reduction</div>
                      <div className="k-checkout-sidebar-right">${data?.downpayment_amount}</div>
                    </div>
                  )}
                  <div className="k-checkout-sidebar-details">
                    <div className="k-checkout-sidebar-left">Tax</div>
                    <div className="k-checkout-sidebar-right">${parseFloat(data?.tax_due_checkout, 10).toFixed(2)}</div>
                  </div>
                  <div className="k-checkout-sidebar-details k-checkout-sidebar-details-highlighted">
                    <div className="k-checkout-sidebar-left">Total Due today</div>
                    <div className="k-checkout-sidebar-right">${parseFloat(data?.due_today, 10).toFixed(2)}</div>
                  </div>
                </div>
                <div className="k-checkout-dark-blue-divider">
                  <div>Pay over time</div>
                  <div>${parseFloat(data?.due_today, 10).toFixed(2)}</div>
                </div>
                <div className="k-checkout-sidebar-subtitle">Leasable items</div>
                {data?.items?.map((el, index) => (
                  // item_code isn't guaranteed unique across items; index is a safe tiebreaker
                  // since this list is a static server-provided render, never reordered/edited.
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={el.item_code + index} className="k-checkout-with-side-margin">
                    <div className="k-checkout-divider" />
                    <div className="k-checkout-sidebar-details">
                      <div className="k-checkout-sidebar-left">{el.item_name}</div>
                      <div className="k-checkout-sidebar-right">QTY{el.quantity}</div>
                    </div>
                    <div className="k-checkout-sidebar-details">
                      <div className="k-checkout-sidebar-left">{el.item_type}</div>
                      <div className="k-checkout-sidebar-right">${el.retailer_price}</div>
                    </div>
                    <div className="k-checkout-sidebar-details">
                      <div className="k-checkout-sidebar-left k-sidebar-cap">{data?.pay_frequency}</div>
                      <div className="k-checkout-sidebar-right">${el.total}</div>
                    </div>
                  </div>
                ))}
                <div className="k-checkout-sidebar-subtitle">Other</div>
                <div className="k-checkout-with-side-margin">
                  <div className="k-checkout-divider" />
                  <div className="k-checkout-sidebar-details">
                    <div className="k-checkout-sidebar-left">Tax</div>
                    <div className="k-checkout-sidebar-right">${data?.tax_90}</div>
                  </div>
                </div>
                <div className="k-checkout-sidebar-subtitle">Total</div>
                <div className="k-checkout-with-side-margin">
                  <div className="k-checkout-divider" />
                  <div className="k-checkout-sidebar-details">
                    <div className="k-checkout-sidebar-left">Cart price</div>
                    <div className="k-checkout-sidebar-right">${data?.leasable_cash_price}</div>
                  </div>
                  <div className="k-checkout-sidebar-details">
                    <div className="k-checkout-sidebar-left k-sidebar-cap">{data?.pay_frequency}</div>
                    <div className="k-checkout-sidebar-right">${data?.monthly_total}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!isMobileView && (
            <div className="k-checkout-back-to-overview">
              <button
                type="button"
                aria-label="back to overview"
                className="k-checkout-back-to-overview-button"
                onClick={viewMoreDetails}
              >
                Back to overview
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

SidebarCheckout.propTypes = {
  data: PropTypes.object,
  checkout: PropTypes.shape({
    checkout_object: PropTypes.shape({
      customer: PropTypes.shape({
        billing: PropTypes.shape({
          first_name: PropTypes.string,
        }),
      }),
    }),
    retailer_name: PropTypes.string,
  }),
  firstname: PropTypes.string,
};
